import React from "react";
import { Route } from "react-router-dom";
import { PermissionRoute } from "@/components/PermissionRoute";
import { usePermissions } from "@/lib/permissions";
import type { RouteConfig } from "@/lib/permissions";

/**
 * Generate protected routes from route configuration
 */
export function generateProtectedRoutes(routeConfigs: RouteConfig[]) {
  return routeConfigs.map((config) => {
    const RouteComponent = config.component;

    return (
      <Route
        key={config.path}
        path={config.path}
        element={
          <PermissionRoute
            permissions={config.permissions}
            fallbackRoute={config.fallbackRoute}
            showUnauthorized={true}
          >
            <RouteComponent />
          </PermissionRoute>
        }
      />
    );
  });
}

/**
 * Hook to get accessible routes for current user
 */
export function useAccessibleRoutes(
  routeConfigs: RouteConfig[]
): RouteConfig[] {
  const { checkPermission } = usePermissions();

  return React.useMemo(() => {
    return routeConfigs.filter((config) => {
      if (!config.permissions) return true;
      return checkPermission(config.permissions);
    });
  }, [routeConfigs, checkPermission]);
}

/**
 * Hook to get accessible menu items from route configurations
 */
export function useAccessibleMenuItems(routeConfigs: RouteConfig[]) {
  const accessibleRoutes = useAccessibleRoutes(routeConfigs);

  return React.useMemo(() => {
    return accessibleRoutes
      .filter((route) => route.menu) // Only routes with menu configuration
      .map((route) => ({
        label: route.menu!.label,
        icon: route.menu!.icon,
        url: route.path,
        group: route.menu!.group,
        order: route.menu!.order || 0,
      }))
      .sort((a, b) => a.order - b.order);
  }, [accessibleRoutes]);
}

/**
 * Component to render routes with automatic permission checking
 */
export function ProtectedRoutes({
  routeConfigs,
}: {
  routeConfigs: RouteConfig[];
}) {
  return <>{generateProtectedRoutes(routeConfigs)}</>;
}
